import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router";
import { useSession } from "hooks/useSession";
import { DASHBOARD_URL } from "constants/app";
import { fetchWrapper } from "utils/apiFetch";

const API_BASE_URL = "https://pro.mokshiq.in";

/*
 * WHAT CHANGED, AND WHY IT MATTERS
 * ================================
 *
 * 1. THE AGREEMENT TEXT IS NO LONGER HARDCODED HERE.
 *
 *    The previous version had all 31 sections written into JSX. The signed PDF
 *    is rendered from Company.details on the server, and every PDF carries a
 *    SHA-256 fingerprint of the exact clause text it was built from. Two copies
 *    of a legal document in two places drift the moment somebody edits one --
 *    and then the PDF attests, under the retailer's OTP, to text the retailer
 *    never actually saw. Both now render from /agreement/company_details, so
 *    the screen and the signed document cannot disagree.
 *
 *    It also means editing a clause is a JSON change, not a frontend release.
 *
 * 2. IT ASKS THE SERVER FOR A SIGNED LINK.
 *
 *    send_otp and accept used to be posted with a bare user_id. Those endpoints
 *    now want a signed token, minted by POST /agreement/link for the
 *    authenticated caller. Nobody can fire an OTP at another retailer's phone
 *    (which costs their admin money) by changing a number in a form body.
 *
 * 3. /agreement/status no longer takes a user_id.
 *
 *    It reads the caller from the token. The old call could read anyone's
 *    acceptance state.
 */

const styles = `
  .agreement-app{
    --primary:#1a56db; --primary-dark:#123a99; --text:#1f2937; --muted:#6b7280;
    --border:#e5e7eb; --bg:#ffffff; --section-bg:#f9fafb; --success:#16a34a;
    --disabled:#9ca3af;
    font-family:"Noto Sans Devanagari","Noto Sans",Poppins,-apple-system,Roboto,Arial,sans-serif;
    color:var(--text); background:var(--bg);
    display:flex; flex-direction:column;
    min-height:100vh; min-height:100dvh;
  }
  .agreement-app *{box-sizing:border-box;}
  .agreement-app header{
    padding:14px 16px; border-bottom:1px solid var(--border);
    background:var(--bg); position:sticky; top:0; z-index:10;
  }
  .agreement-app header h1{font-size:16px;margin:0 0 2px 0;font-weight:700;}
  .agreement-app header p{margin:0;font-size:12px;color:var(--muted);}
  .agreement-app #scrollProgress{height:3px;background:var(--border);position:relative;overflow:hidden;}
  .agreement-app #scrollProgressBar{height:100%;background:var(--primary);transition:width .1s linear;}
  .agreement-app #content{
    flex:1; overflow-y:auto; -webkit-overflow-scrolling:touch;
    padding:16px; font-size:14px; line-height:1.65;
  }
  .agreement-app #content h2{
    font-size:15px; color:var(--primary-dark); margin:22px 0 8px 0;
    padding-bottom:6px; border-bottom:1px solid var(--border);
  }
  .agreement-app #content h2:first-child{margin-top:0;}
  .agreement-app #content h3{font-size:14px;margin:14px 0 6px 0;}
  .agreement-app #content p{margin:6px 0;}
  .agreement-app #content p .cn{color:var(--muted);font-size:12px;margin-right:6px;
    font-variant-numeric:tabular-nums;}
  .agreement-app #content ul{margin:6px 0;padding-left:20px;}
  .agreement-app #content li{margin:4px 0;}
  .agreement-app .note{
    background:var(--section-bg); border-left:3px solid var(--primary);
    padding:10px 12px; border-radius:0 8px 8px 0; color:var(--muted);
    font-size:13px; margin:10px 0;
  }
  .agreement-app .field-line{
    margin:10px 0; padding-bottom:4px; border-bottom:1px dashed var(--border);
    color:var(--muted); font-size:13px;
  }
  .agreement-app .decl-box{
    background:var(--section-bg); border:1px solid var(--border);
    border-radius:10px; padding:12px 14px; margin:8px 0;
  }
  .agreement-app .decl-item{
    display:flex; align-items:flex-start; gap:10px; padding:8px 0;
    border-bottom:1px solid var(--border);
  }
  .agreement-app .decl-item:last-child{border-bottom:none;}
  .agreement-app .decl-item input[type="checkbox"]{
    width:20px;height:20px;margin-top:2px;flex-shrink:0;accent-color:var(--primary);
  }
  .agreement-app .decl-item label{font-size:13.5px;line-height:1.5;}
  .agreement-app #bottomAnchor{height:1px;}
  .agreement-app footer{
    border-top:1px solid var(--border);
    padding:12px 16px calc(12px + env(safe-area-inset-bottom));
    background:var(--bg); position:sticky; bottom:0;
  }
  .agreement-app #hint{font-size:12px;color:var(--muted);text-align:center;margin:0 0 8px 0;}
  .agreement-app #acceptBtn{
    width:100%; padding:14px; border:none; border-radius:10px;
    background:var(--disabled); color:#fff; font-size:15px; font-weight:700; letter-spacing:.2px;
  }
  .agreement-app #acceptBtn.enabled{background:var(--primary);}
  .agreement-app #acceptBtn.enabled:active{background:var(--primary-dark);}
  .agreement-app #acceptBtn:disabled{cursor:not-allowed;}
  .agreement-app .loadState{
    flex:1; display:flex; flex-direction:column; align-items:center;
    justify-content:center; gap:14px; padding:32px; text-align:center; color:var(--muted);
  }
  .agreement-app .loadState button{
    padding:11px 22px; border:none; border-radius:9px;
    background:var(--primary); color:#fff; font-weight:600;
  }
  #otpOverlay{
    display:flex; position:fixed; inset:0; background:rgba(17,24,39,.55);
    align-items:center; justify-content:center; padding:16px; z-index:9999;
  }
  #otpSheet{
    width:100%; max-width:400px; max-height:calc(100dvh - 32px); overflow-y:auto;
    background:#fff; border-radius:16px; padding:22px;
    box-shadow:0 20px 60px rgba(0,0,0,.35);
    font-family:"Noto Sans Devanagari","Noto Sans",Poppins,-apple-system,Roboto,Arial,sans-serif;
    color:#1f2937;
  }
  #otpSheet h3{margin:0 0 4px 0;font-size:16px;}
  #otpSheet p{margin:0 0 16px 0;font-size:13px;color:#6b7280;}
  #otpInput{
    width:100%; font-size:22px; letter-spacing:8px; text-align:center; padding:12px;
    border:1px solid #e5e7eb; border-radius:10px; margin-bottom:10px; box-sizing:border-box;
  }
  #otpError{color:#dc2626;font-size:12.5px;min-height:16px;margin-bottom:8px;}
  #otpVerifyBtn{
    width:100%; padding:13px; border:none; border-radius:10px; background:#1a56db;
    color:#fff; font-size:15px; font-weight:700; margin-bottom:8px;
  }
  #otpVerifyBtn:disabled{background:#9ca3af;}
  #otpResendRow{display:flex;justify-content:space-between;align-items:center;font-size:13px;}
  #otpResendBtn{background:none;border:none;color:#1a56db;font-weight:600;padding:6px 0;}
  #otpResendBtn:disabled{color:#9ca3af;}
  #otpCancelBtn{background:none;border:none;color:#6b7280;font-size:13px;padding:6px 0;}
`;

const HI = (d) => String(d?.agreement?.language || "").toLowerCase().startsWith("hi");

const AgreementScreen = ({ companyUrl = "csc.kryntiq.com" }) => {
  const navigate = useNavigate();
  const { userid, role } = useSession();

  const contentRef = useRef(null);
  const otpInputRef = useRef(null);
  const resendIntervalRef = useRef(null);

  // ── server state ────────────────────────────────────────────────────────
  const [booting, setBooting] = useState(true);
  const [bootError, setBootError] = useState("");
  const [token, setToken] = useState("");          // the signed link token
  const [details, setDetails] = useState(null);    // company + agreement JSON
  // Raw responses, shown only with ?agreementDebug=1 in the URL. A page that
  // renders nothing must be able to say why without a rebuild.
  const [raw, setRaw] = useState({});
  const [userInfo, setUserInfo] = useState(null);

  const agreement = details?.agreement;
  const sections = agreement?.sections || [];
  const declarations = agreement?.declarations || [];

  // ── ui state ────────────────────────────────────────────────────────────
  const [scrollPct, setScrollPct] = useState(0);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [checks, setChecks] = useState([]);
  const allChecked = checks.length > 0 && checks.every(Boolean);
  const ready = allChecked;

  const [alreadyAccepted, setAlreadyAccepted] = useState(false);
  const [acceptedAtLabel, setAcceptedAtLabel] = useState("");
  const [hint, setHint] = useState("");

  const [sendingOtp, setSendingOtp] = useState(false);
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpSubtitle, setOtpSubtitle] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);

  useEffect(() => {
    setChecks(Array(declarations.length).fill(false));
  }, [declarations.length]);

  // ── boot: signed link -> company details -> existing acceptance ─────────
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setBooting(true);
      setBootError("");
      try {
        // 1. mint the signed link. fetchWrapper must attach the Bearer token.
        const body = new URLSearchParams();
        if (userid) body.append("user_id", userid);
        if (companyUrl) body.append("company_url", companyUrl);

        const linkRes = await fetchWrapper(`${API_BASE_URL}/agreement/link`, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: body.toString(),
        });

        if (!linkRes?.status || !linkRes?.data?.url) {
          throw new Error(linkRes?.message || "Could not open the agreement.");
        }

        // The token is the only part the API endpoints need.
        const signed = new URL(linkRes.data.url).searchParams.get("t");
        if (!signed) throw new Error("Agreement link was malformed.");
        if (cancelled) return;
        setToken(signed);
        setRaw((r) => ({ ...r, link: linkRes }));

        // 2. the agreement content itself
        const q = companyUrl ? `?company_url=${encodeURIComponent(companyUrl)}` : "";
        const detRes = await fetchWrapper(`${API_BASE_URL}/agreement/company_details${q}`);
        if (!detRes?.status || !detRes?.data) {
          throw new Error(detRes?.message || "Could not load the agreement content.");
        }
        if (cancelled) return;
        setDetails(detRes.data);
        console.info("[agreement] loaded",
          {
            company: detRes.data?.legal_name,
            sections: detRes.data?.agreement?.sections?.length ?? 0,
            declarations: detRes.data?.agreement?.declarations?.length ?? 0,
            language: detRes.data?.agreement?.language
          });
        setRaw((r) => ({ ...r, details: detRes }));

        // 3. the retailer's own details, for the block at the end
        try {
          const uRes = await fetchWrapper(`${API_BASE_URL}/agreement/user_details`);
          if (!cancelled && uRes?.status && uRes.data) setUserInfo(uRes.data);
        } catch (e) {
          // Not fatal -- the agreement is still readable and signable without it.
          console.warn("[agreement] user_details unavailable", e);
        }

        // 4. already signed?
        const stRes = await fetchWrapper(`${API_BASE_URL}/agreement/status`);
        if (!cancelled && stRes?.status && stRes.accepted) {
          setAlreadyAccepted(true);
          setAcceptedAtLabel(stRes.data?.accepted_at || "");
        }
      } catch (err) {
        console.error("agreement boot failed", err);
        if (!cancelled) setBootError(err?.message || "Could not open the agreement.");
      } finally {
        // NOT guarded by `cancelled`. If the effect is torn down mid-flight
        // (React 18 StrictMode double-mounts, or userid resolving late changes
        // the deps) a guarded setBooting leaves the screen on "Loading…"
        // forever with no error anywhere -- which is exactly the silent blank
        // page this was reported as. A state update after unmount is harmless
        // in React 18; a permanently stuck loader is not.
        setBooting(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userid, companyUrl]);

  // ── scroll progress ─────────────────────────────────────────────────────
  const handleScroll = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    const max = el.scrollHeight - el.clientHeight;
    const pct = max > 0 ? Math.min(100, (el.scrollTop / max) * 100) : 100;
    setScrollPct(pct);
    if (pct >= 98) setScrolledToBottom(true);
  }, []);

  useEffect(() => {
    handleScroll();
    window.addEventListener("resize", handleScroll);
    return () => window.removeEventListener("resize", handleScroll);
  }, [handleScroll, details]);

  // ── hint ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const hi = HI(details);
    if (alreadyAccepted) {
      setHint(hi ? "आपने यह Agreement पहले ही स्वीकार कर लिया है"
        : "You have already accepted this agreement");
    } else if (!allChecked) {
      setHint(hi ? "कृपया सभी घोषणाओं के बॉक्स चेक करें"
        : "Please tick every declaration");
    } else {
      setHint(hi ? "आप अब Agreement स्वीकार कर सकते हैं"
        : "You can now accept the agreement");
    }
  }, [allChecked, alreadyAccepted, details]);

  useEffect(() => () => clearInterval(resendIntervalRef.current), []);

  const toggleCheck = (idx) =>
    setChecks((prev) => {
      const next = [...prev];
      next[idx] = !next[idx];
      return next;
    });

  const currentPlatform = () => {
    if (/Android/i.test(navigator.userAgent)) return "android";
    if (/iPhone|iPad/i.test(navigator.userAgent)) return "ios";
    return "web";
  };

  const startResendCountdown = (seconds) => {
    setResendSeconds(seconds);
    clearInterval(resendIntervalRef.current);
    resendIntervalRef.current = setInterval(() => {
      setResendSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(resendIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // ── Step 1: OTP ─────────────────────────────────────────────────────────
  const requestOtp = async (isResend) => {
    if (!token) return;
    if (!isResend) setSendingOtp(true);
    const hi = HI(details);

    try {
      const form = new URLSearchParams();
      form.append("t", token);           // signed; no user_id

      const res = await fetchWrapper(`${API_BASE_URL}/agreement/send_otp`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: form.toString(),
      });

      if (!res?.status) {
        // 429 carries retry_after -- the server enforces a 60s floor between
        // sends because every SMS is billed to the retailer's admin.
        const msg = res?.message ||
          (hi ? "OTP भेजने में समस्या हुई, कृपया पुनः प्रयास करें" : "Could not send the OTP");
        setOtpError(msg);
        if (!otpOpen) setHint(msg);
        if (res?.retry_after) startResendCountdown(Number(res.retry_after));
        return;
      }

      const masked = res.data?.phone_masked || "";
      setOtpSubtitle(
        masked
          ? (hi ? `OTP आपके मोबाइल नंबर ${masked} पर भेजा गया है।`
            : `An OTP has been sent to ${masked}.`)
          : (hi ? "आपके रजिस्टर्ड मोबाइल नंबर पर OTP भेजा गया है।"
            : "An OTP has been sent to your registered mobile number.")
      );

      if (!isResend) {
        setOtpValue("");
        setOtpError("");
        setOtpOpen(true);
        setTimeout(() => otpInputRef.current?.focus(), 50);
      } else {
        setOtpError("");
      }
      startResendCountdown(60);   // matches OTP_RESEND_COOLDOWN_SECONDS
    } catch (err) {
      console.error("send_otp failed", err);
      setOtpError(hi ? "नेटवर्क समस्या, कृपया पुनः प्रयास करें" : "Network problem, please retry");
    } finally {
      if (!isResend) setSendingOtp(false);
    }
  };

  // ── Step 2: verify + accept ─────────────────────────────────────────────
  const verifyAndAccept = async () => {
    const hi = HI(details);
    const otp = otpValue.trim();
    if (!otp) {
      setOtpError(hi ? "कृपया OTP दर्ज करें" : "Please enter the OTP");
      return;
    }

    setVerifying(true);
    setOtpError("");

    try {
      const form = new URLSearchParams();
      form.append("t", token);
      form.append("otp", otp);
      form.append("user_id", userid);
      form.append("agreement_version", agreement?.version || "v1.0");
      form.append("scrolled_fully", "true");
      form.append("all_declarations_checked", "true");
      form.append("platform", currentPlatform());

      const res = await fetchWrapper(`${API_BASE_URL}/agreement/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: form.toString(),
      });

      if (!res?.status) {
        setOtpError(res?.message ||
          (hi ? "गलत या समाप्त हो चुका OTP" : "Wrong or expired OTP"));
        return;
      }

      clearInterval(resendIntervalRef.current);
      setOtpOpen(false);
      setAlreadyAccepted(true);
      setAcceptedAtLabel(res.data?.accepted_at || "");
      finishAccept(res.data);
    } catch (err) {
      console.error("accept failed", err);
      setOtpError(hi ? "नेटवर्क समस्या, कृपया पुनः प्रयास करें" : "Network problem, please retry");
    } finally {
      setVerifying(false);
    }
  };

  // ── tell any host (WebView / iframe), then move on ──────────────────────
  const finishAccept = (apiData) => {
    const payload = {
      status: true,
      agreementVersion: apiData?.agreement_version || agreement?.version || "v1.0",
      documentNumber: apiData?.document_number || "",
      whatsappStatus: apiData?.whatsapp_status || "",
      acceptedAt: apiData?.accepted_at || new Date().toISOString(),
      allDeclarationsChecked: true,
      scrolledFully: true,
    };

    const emit = (fn) => { try { fn(); } catch (e) { /* never block the redirect */ } };

    emit(() => window.AgreementChannel?.postMessage?.(JSON.stringify(payload)));
    emit(() => window.flutter_inappwebview?.callHandler?.("onAgreementAccepted", payload));
    emit(() => {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ type: "agreementAccepted", payload }, "*");
      }
    });
    emit(() => window.onAgreementAccepted?.(payload));
    emit(() => document.dispatchEvent(
      new CustomEvent("agreementAccepted", { detail: payload })));

    setTimeout(() => navigate(DASHBOARD_URL[role] || "/", { replace: true }), 600);
  };

  const handleCancelOtp = () => {
    clearInterval(resendIntervalRef.current);
    setOtpOpen(false);
  };

  const handleAcceptClick = () => {
    if (!ready || alreadyAccepted || sendingOtp || !token) return;
    requestOtp(false);
  };

  // ── clause renderer ─────────────────────────────────────────────────────
  // Mirrors the server's numbering exactly: only string clauses and
  // untitled {text,bullets} objects take a number; lettered blocks and notes
  // do not. Get this wrong and the screen says 9.3 where the signed PDF says
  // 9.4 for the same sentence.
  const renderSection = (section, index) => {
    const num = section.number || index + 1;
    let counter = 0;

    return (
      <React.Fragment key={`${num}-${section.title}`}>
        <h2>{num}. {section.title}</h2>
        {(section.clauses || []).map((clause, i) => {
          if (typeof clause === "string") {
            counter += 1;
            return (
              <p key={i}><span className="cn">{num}.{counter}</span>{clause}</p>
            );
          }
          if (clause?.note) {
            return <p className="note" key={i}>{clause.note}</p>;
          }
          const bullets = clause?.bullets?.length ? (
            <ul>{clause.bullets.map((b, k) => <li key={k}>{b}</li>)}</ul>
          ) : null;

          if (clause?.label) {
            return (
              <React.Fragment key={i}>
                <h3>{clause.label}. {clause.text}</h3>
                {bullets}
              </React.Fragment>
            );
          }
          counter += 1;
          return (
            <React.Fragment key={i}>
              <p><span className="cn">{num}.{counter}</span>{clause?.text}</p>
              {bullets}
            </React.Fragment>
          );
        })}
      </React.Fragment>
    );
  };

  // ── render ──────────────────────────────────────────────────────────────
  const hi = HI(details);

  const debug =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("agreementDebug");

  const DebugPanel = () =>
    debug ? (
      <pre
        style={{
          margin: 16, padding: 12, background: "#0d0f14", color: "#9ae6b4",
          fontSize: 11, borderRadius: 8, overflow: "auto", maxHeight: 320,
        }}
      >
        {JSON.stringify(
          {
            userid,
            companyUrl: companyUrl || "(none — server derives it)",
            tokenPresent: !!token,
            sections: sections.length,
            declarations: declarations.length,
            raw,
          },
          null,
          2
        )}
      </pre>
    ) : null;

  if (booting || bootError) {
    return (
      <>
        <style>{styles}</style>
        <div className="agreement-app">
          <div className="loadState">
            {bootError ? (
              <>
                <div style={{ fontSize: 15, color: "#b45309" }}>{bootError}</div>
                <button onClick={() => window.location.reload()}>
                  {hi ? "पुनः प्रयास करें" : "Try again"}
                </button>
              </>
            ) : (
              <div>{hi ? "लोड हो रहा है…" : "Loading…"}</div>
            )}
          </div>
          <DebugPanel />
        </div>
      </>
    );
  }

  // The API always returns at least the default clause set, so zero sections
  // means the company's stored details JSON is malformed -- most often
  // `agreement` saved as a string instead of an object, or `sections` replaced
  // with []. Say that plainly rather than rendering an empty page that looks
  // like a frontend bug.
  if (sections.length === 0) {
    return (
      <>
        <style>{styles}</style>
        <div className="agreement-app">
          <div className="loadState">
            <div style={{ fontSize: 15, color: "#b45309", maxWidth: 460 }}>
              This company has no agreement content configured
              {details?.legal_name ? ` (${details.legal_name})` : ""}.
              <div style={{ fontSize: 13, marginTop: 8, color: "#6b7280" }}>
                company_url: <code>{companyUrl || "(derived by the server)"}</code>
                <br />
                Check that <code>Company.details.agreement.sections</code> is a
                non-empty list on this company row.
              </div>
            </div>
          </div>
          <DebugPanel />
        </div>
      </>
    );
  }

  const acceptBtnLabel = alreadyAccepted
    ? `${hi ? "स्वीकृत" : "Accepted"} ✓${acceptedAtLabel ? "  (" + acceptedAtLabel + ")" : ""}`
    : sendingOtp
      ? (hi ? "OTP भेजा जा रहा है..." : "Sending OTP...")
      : (hi ? "मैंने पढ़ लिया है और स्वीकार करता/करती हूँ" : "I have read and accept");

  return (
    <>
      <style>{styles}</style>
      <div className="agreement-app" lang={agreement?.language || "en"}>
        <header>
          <h1>{agreement?.title || "User / Retailer Service Agreement"}</h1>
          <p>
            {details?.legal_name}
            {agreement?.version ? ` · ${agreement.version}` : ""}
          </p>
        </header>

        <div id="scrollProgress">
          <div id="scrollProgressBar" style={{ width: `${scrollPct}%` }} />
        </div>

        <div id="content" ref={contentRef} onScroll={handleScroll}>
          {sections.map(renderSection)}

          {declarations.length > 0 && (
            <>
              <h2>
                {agreement?.declaration_number ? `${agreement.declaration_number}. ` : ""}
                {agreement?.declaration_heading ||
                  (hi ? "घोषणा एवं स्वीकृति" : "Declarations")}
              </h2>
              {agreement?.declaration_lead && <p>{agreement.declaration_lead}</p>}

              <div className="decl-box">
                {declarations.map((text, idx) => (
                  <div className="decl-item" key={idx}>
                    <input
                      type="checkbox"
                      id={`d${idx + 1}`}
                      checked={!!checks[idx]}
                      onChange={() => toggleCheck(idx)}
                    />
                    <label htmlFor={`d${idx + 1}`}>{text}</label>
                  </div>
                ))}
              </div>
            </>
          )}

          {details?.grievance_officer?.name && (
            <>
              <h2>{hi ? "शिकायत निवारण" : "Grievance Redressal"}</h2>
              <div className="field-line">
                {details.grievance_officer.name}, {details.grievance_officer.designation}
              </div>
              {details.grievance_officer.email && (
                <div className="field-line">{details.grievance_officer.email}</div>
              )}
              {details.grievance_officer.phone && (
                <div className="field-line">{details.grievance_officer.phone}</div>
              )}
            </>
          )}

          {userInfo && (
            <>
              <h2>{hi ? "यूज़र का विवरण" : "Retailer details"}</h2>
              <div className="field-line">
                {hi ? "नाम" : "Name"}: {userInfo.user_name || "—"}
              </div>
              <div className="field-line">
                {hi ? "दुकान/व्यवसाय" : "Business"}: {userInfo.business_name || "—"}
              </div>
              <div className="field-line">
                {hi ? "मोबाइल नंबर" : "Mobile"}: {userInfo.mobile || "—"}
              </div>
              <div className="field-line">
                {hi ? "ईमेल आईडी" : "Email"}: {userInfo.email || "—"}
              </div>
              <div className="field-line">PAN: {userInfo.pan_card || "—"}</div>
              {userInfo.aadhar_masked && (
                <div className="field-line">
                  {hi ? "आधार" : "Aadhaar"}: {userInfo.aadhar_masked}
                </div>
              )}
              <div className="field-line">
                {hi ? "पूरा पता" : "Address"}:{" "}
                {[userInfo.address, userInfo.city, userInfo.state, userInfo.pin_code]
                  .filter(Boolean)
                  .join(", ") || "—"}
              </div>
              <div className="field-line">
                {hi ? "स्वीकृति की तारीख" : "Accepted at"}: {acceptedAtLabel || "—"}
              </div>
            </>
          )}

          <DebugPanel />
          <div id="bottomAnchor" />
        </div>

        <footer>
          <p id="hint">{hint}</p>
          <button
            id="acceptBtn"
            className={ready && !alreadyAccepted ? "enabled" : ""}
            disabled={!ready || alreadyAccepted || sendingOtp}
            onClick={handleAcceptClick}
          >
            {acceptBtnLabel}
          </button>
        </footer>
      </div>

      {otpOpen &&
        createPortal(
          <div id="otpOverlay" onClick={(e) => e.target.id === "otpOverlay" && handleCancelOtp()}>
            <div id="otpSheet">
              <h3>{hi ? "OTP से पुष्टि करें" : "Confirm with OTP"}</h3>
              <p>{otpSubtitle}</p>
              <input
                ref={otpInputRef}
                type="tel"
                id="otpInput"
                maxLength={8}
                inputMode="numeric"
                placeholder="― ― ― ― ― ―"
                autoComplete="one-time-code"
                value={otpValue}
                onChange={(e) => setOtpValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && verifyAndAccept()}
                autoFocus
              />
              <div id="otpError">{otpError}</div>
              <button id="otpVerifyBtn" onClick={verifyAndAccept} disabled={verifying}>
                {verifying
                  ? (hi ? "पुष्टि हो रही है..." : "Verifying...")
                  : (hi ? "पुष्टि करें और स्वीकार करें" : "Verify & Accept")}
              </button>
              <div id="otpResendRow">
                <button
                  id="otpResendBtn"
                  disabled={resendSeconds > 0}
                  onClick={() => requestOtp(true)}
                >
                  {resendSeconds > 0
                    ? `${hi ? "OTP दोबारा भेजें" : "Resend OTP"} (${resendSeconds}s)`
                    : (hi ? "OTP दोबारा भेजें" : "Resend OTP")}
                </button>
                <button id="otpCancelBtn" onClick={handleCancelOtp}>
                  {hi ? "रद्द करें" : "Cancel"}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default AgreementScreen;